import React, { useCallback, useEffect, useState } from "react";
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import styled from "@emotion/styled";
import { useSelector } from "react-redux";
import SelectedMemberInfo from "./components/SelectedMemberInfo";
import SelectedMemberChart from "./components/SelectedMemberChart";
import SelectedMemberStats from "./components/SelectedMemberStats";
import { selectTeamMembersByName, selectTeamMembersInfo, selectTeamMembersMonthlyStats } from "../../../../slices/teamMembersSlice";

const MemberPerformance = () => {

  const [selectedMemberId, setSelectedMemberId] = useState('')
  const [selectedMemberInfoObj, setSelectedMemberInfoObj] = useState({})
  const [selectedMemberFirstName, setSelectedMemberFirstName] = useState('')
  const [selectedMemberMonthlyStats, setSelectedMemberMonthlyStats] = useState({})

  const teamMembersMonthlyStats = useSelector(selectTeamMembersMonthlyStats)
  const teamMembersByName = useSelector(selectTeamMembersByName)
  const teamMembersInfo = useSelector(selectTeamMembersInfo)

  const handleMemberChange = useCallback((memberId) => {
    if (!teamMembersByName.length) {
      return
    }

    let thisMember = teamMembersByName.find(member => member.memberId === memberId)
    if (thisMember) {
      setSelectedMemberId(thisMember.memberId)
      setSelectedMemberFirstName(thisMember.firstName)
      let thisMemberInfo = teamMembersInfo.find(member => member.memberId === memberId)
      setSelectedMemberInfoObj(thisMemberInfo)
      let thisMemberMonthlyStats = teamMembersMonthlyStats.find(member => member.memberId === memberId)
      setSelectedMemberMonthlyStats(thisMemberMonthlyStats.memberMonthlyStats)
    } else {
      setSelectedMemberId(teamMembersByName[0].memberId)
      setSelectedMemberFirstName(teamMembersByName[0].firstName)
      setSelectedMemberInfoObj(teamMembersInfo[0])
      setSelectedMemberMonthlyStats(teamMembersMonthlyStats[0].memberMonthlyStats)
    }
  }, [teamMembersByName, teamMembersInfo, teamMembersMonthlyStats])

  useEffect(() => {
    if (!teamMembersByName.length) {
      return
    }

    handleMemberChange(teamMembersByName[0].memberId)
  }, [handleMemberChange, teamMembersByName])

  return (
    <MemberPerformancePage>
      <MemberPerformanceWorkspace>
        <WorkspaceHeader>
          <div>
            <WorkspaceEyebrow>Team Analytics</WorkspaceEyebrow>
            <WorkspaceTitle>Member Performance</WorkspaceTitle>
          </div>
          <WorkspaceSummary>
            Review individual contribution, closure velocity, and monthly investigation outcomes by team member.
          </WorkspaceSummary>
        </WorkspaceHeader>

        <WorkspaceBody>
          <MemberRail>
            <RailHeader>
              <RailTitle>Team Members</RailTitle>
              <RailCount>{teamMembersByName.length}</RailCount>
            </RailHeader>
            <MemberList aria-label="Team member selector">
              {teamMembersByName.map((member) => (
                member.role === 'user' &&
                <MemberButton
                  key={member.memberId}
                  type="button"
                  className={selectedMemberId === member.memberId ? 'active' : ''}
                  aria-pressed={selectedMemberId === member.memberId}
                  onClick={() => handleMemberChange(member.memberId)}
                >
                  <MemberIcon className={member.role === 'admin' ? 'admin' : 'user'}>
                    <AccountBoxIcon />
                  </MemberIcon>
                  <MemberName>{member.fullName}</MemberName>
                  <MemberRole>User</MemberRole>
                </MemberButton>
              ))}
              {teamMembersByName.map((member) => (
                member.role === 'admin' &&
                <MemberButton
                  key={member.memberId}
                  type="button"
                  className={selectedMemberId === member.memberId ? 'active' : ''}
                  aria-pressed={selectedMemberId === member.memberId}
                  onClick={() => handleMemberChange(member.memberId)}
                >
                  <MemberIcon className={member.role === 'admin' ? 'admin' : 'user'}>
                    <AccountBoxIcon />
                  </MemberIcon>
                  <MemberName>{member.fullName}</MemberName>
                  <MemberRole>Manager</MemberRole>
                </MemberButton>
              ))}
            </MemberList>
            <SelectedMemberInfo selectedMemberInfoObj={selectedMemberInfoObj} />
          </MemberRail>

          <AnalysisColumn>
            <SelectedMemberStats selectedMemberInfoObj={selectedMemberInfoObj} selectedMemberFirstName={selectedMemberFirstName} />
            <SelectedMemberChart selectedMemberMonthlyStats={selectedMemberMonthlyStats} selectedMemberFirstName={selectedMemberFirstName} />
          </AnalysisColumn>
        </WorkspaceBody>
      </MemberPerformanceWorkspace>
    </MemberPerformancePage>
  )
}

const MemberPerformancePage = styled.main(({ theme }) => `
  width: 100%;
  height: 100%;
  min-height: 0;
  display: grid;
  padding: 1rem;
  overflow: hidden;
  background: ${theme.palette.common.pageBackground};
`)

const MemberPerformanceWorkspace = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.9rem;
  background: ${theme.palette.common.cardBackground};
  box-shadow: inset 0 0.1rem 0 rgba(255, 255, 255, 0.72);
`)

const WorkspaceHeader = styled.header(({ theme }) => `
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.45rem;
  padding: 1.35rem 1.5rem 1.1rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.white};
`)

const WorkspaceEyebrow = styled.div(({ theme }) => `
  margin-bottom: 0.25rem;
  color: ${theme.palette.primary.main};
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04rem;
  text-transform: uppercase;
`)

const WorkspaceTitle = styled.h1(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1.7rem;
  font-weight: 800;
  line-height: 1.15;
`)

const WorkspaceSummary = styled.p(({ theme }) => `
  max-width: 58rem;
  margin: 0;
  color: ${theme.palette.common.grayText};
  font-size: 0.96rem;
  font-weight: 550;
  line-height: 1.45;
`)

const WorkspaceBody = styled.div(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-columns: minmax(16rem, 20rem) minmax(0, 1fr);
  gap: 1rem;
  padding: 1.1rem;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${theme.palette.common.pageBackground};

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

const MemberRail = styled.aside(({ theme }) => `
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(8rem, 1fr) auto;
  align-self: start;
  max-height: 100%;
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

const RailHeader = styled.div(({ theme }) => `
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.9rem 1rem;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

const RailTitle = styled.h2(({ theme }) => `
  margin: 0;
  color: ${theme.palette.common.black};
  font-size: 1rem;
  font-weight: 800;
  line-height: 1.2;
`)

const RailCount = styled.span(({ theme }) => `
  min-width: 1.8rem;
  padding: 0.2rem 0.45rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 999rem;
  background: ${theme.palette.common.white};
  color: ${theme.palette.common.grayText};
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1;
  text-align: center;
`)

const MemberList = styled.div(({ theme }) => `
  min-height: 0;
  display: grid;
  align-content: start;
  gap: 0.45rem;
  padding: 0.75rem;
  overflow-x: hidden;
  overflow-y: auto;

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:vertical {
    width: 0.35rem;
  }
`)

const MemberButton = styled.button(({ theme }) => `
  width: 100%;
  min-width: 0;
  min-height: 3.15rem;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.65rem;
  padding: 0.55rem 0.7rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.7rem;
  background: ${theme.palette.common.white};
  color: ${theme.palette.common.black};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease, box-shadow 0.16s ease;

  &:hover {
    border-color: ${theme.palette.common.grayBorderDark};
    background: ${theme.palette.common.cardBackground};
  }

  &:focus-visible {
    outline: 0.18rem solid ${theme.palette.primary.labelTransparent};
    outline-offset: 0.12rem;
  }

  &.active {
    border-color: ${theme.palette.primary.main};
    background: ${theme.palette.primary.labelTransparent};
    box-shadow: inset 0.22rem 0 0 ${theme.palette.primary.main};
    cursor: default;
  }
`)

const MemberIcon = styled.span(({ theme }) => `
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.6rem;
  background: ${theme.palette.common.grayBorderLight};
  color: ${theme.icon.primary.primary};

  &.admin {
    color: ${theme.icon.secondary.primary};
  }

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`)

const MemberName = styled.span(({ theme }) => `
  min-width: 0;
  overflow: hidden;
  color: ${theme.palette.common.black};
  font-size: 0.92rem;
  font-weight: 750;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
`)

const MemberRole = styled.span(({ theme }) => `
  color: ${theme.palette.common.grayText};
  font-size: 0.72rem;
  font-weight: 800;
  line-height: 1;
  text-transform: uppercase;
`)

const AnalysisColumn = styled.section(`
  min-width: 0;
  display: grid;
  grid-template-rows: auto minmax(26rem, 1fr);
  gap: 1rem;
`)

export default MemberPerformance
